"use client"

import clsx from "clsx"
import { Fragment, useEffect, useState } from "react"
import { useIsClient } from "src/hooks/useIsClient"
import { getDateInGermany, getEndOfDayInGermany, interpretAsGermanTimeIso } from "src/utilities/germanTime"

function getReferenceDate(until: string | null | undefined) {
  if (!until || !new Date(until).getTime()) {
    return getEndOfDayInGermany()
  }
  if (until.endsWith("Z") || until.includes("+")) {
    return getDateInGermany(until)
  }
  return new Date(interpretAsGermanTimeIso(until))
}

export type CountdownProps = {
  until: string | null | undefined
}

export const Countdown = ({ until }: CountdownProps) => {
  // start at 24 hours
  const [remaining, setRemaining] = useState(0)
  const isClient = useIsClient()

  useEffect(() => {
    function tick() {
      const currentDate = new Date()
      const referenceDate = getReferenceDate(until)
      const totalMilliseconds = referenceDate.getTime() - currentDate.getTime()

      setRemaining(totalMilliseconds)
    }

    tick()

    const interval = setInterval(tick, 1000)

    return () => clearInterval(interval)
  }, [until])

  if (remaining <= 0) {
    return (
      <div
        className={clsx(
          "text-default flex h-8 items-center justify-center rounded-md bg-white px-4 font-mono uppercase",
          { invisible: !isClient },
        )}
      >
        Abgelaufen
      </div>
    )
  }

  const days = Math.floor(remaining / (1000 * 60 * 60 * 24))
  const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((remaining % (1000 * 60)) / 1000)

  const parts = [
    days > 0 ? days.toString().padStart(2, "0") + "d" : undefined,
    days > 0 || hours > 0 ? hours.toString().padStart(2, "0") + "h" : undefined,
    days > 0 || hours > 0 || minutes > 0 ? minutes.toString().padStart(2, "0") + "m" : undefined,
    seconds.toString().padStart(2, "0") + "s",
  ].filter(Boolean)

  return (
    <div className={clsx("flex items-center justify-center font-mono", { invisible: !isClient })}>
      {parts.map((part, index) => (
        <Fragment key={index}>
          {index > 0 && <div className="text-default flex h-4 w-0.5 items-center justify-center bg-white">:</div>}
          <div className="text-default flex h-8 w-12 items-center justify-center bg-white first:rounded-l-md last:rounded-r-md">
            {part}
          </div>
        </Fragment>
      ))}
    </div>
  )
}
